import React from 'react';
import moment from 'moment';
import { Button, Input } from '../..';
import useDropdownTrigger from '../../../hooks/useDropdownTrigger';

export type DateRange = {
  showRange: boolean;
  start: string;
  end: string;
  type:
    | 'today'
    | 'yesterday'
    | 'last_7_days'
    | 'last_30_days'
    | 'this_month'
    | 'last_month'
    | 'custom';
};
type ModalProps = {
  label: string;
  onApplyDate: (date?: DateRange) => void;
};

function DateRangePicker({ label, onApplyDate }: ModalProps) {
  const [dates, setDates] = React.useState<DateRange>({
    showRange: false,
    start: moment().format('YYYY-MM-DD'),
    end: moment().add(1, 'day').format('YYYY-MM-DD'),
    type: 'today',
  });
  const optionsMenuRef = React.useRef<HTMLDivElement>(null);
  const { isExpanded, trigger, getTriggerProps } =
    useDropdownTrigger(optionsMenuRef);

  const handleDateInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { name, value } = event.target;

    setDates(fndDate => ({
      ...fndDate,
      [name]: value,
    }));
  };

  const handleOnApplyDate = () => {
    onApplyDate({
      ...dates,
      ...(!dates.showRange && {
        end: '',
      }),
    });
    trigger();
  };

  return (
    <div className="relative hidden tablet:block" ref={optionsMenuRef}>
      <button
        type="button"
        onClick={trigger}
        className="px-4 min-w-[180px] h-[40px] text-pos-yellow
      border border-[#8f7319] bg-[#8f7319] rounded-md shrink-0 font-medium hover:opacity-[0.85] 
      text-xbase"
        id="user-menu-button"
        aria-expanded="false"
        aria-haspopup="true"
      >
        {label}
      </button>

      <div
        className={`absolute transition-all ease-in-out overflow-hidden z-50 w-[400px] mt-1 rounded-md shadow-[0_0px_15px_0px_rgba(0,0,0,0.08)] left-0
          bg-white border-[0.5px] border-[#e9ecef] ring-[#e9ecef] ring-opacity-5 focus:outline-none 
          ${!isExpanded ? 'h-0 invisible border-none' : ''}`}
      >
        <div className="px-4 pt-4 pb-5">
          <h3 className="text-pos-black text-base font-semibold inline-flex whitespace-pre">
            Pick Date
          </h3>
          <div className="mt-3">
            <div className="flex flex-row space-x-1 mt-3 w-full items-center">
              <Input
                id="start"
                name="start"
                type="date"
                value={dates.start}
                onChange={handleDateInputChange}
                className="flex-1 min-w-0"
              />
              {dates.showRange && (
                <>
                  <span className="font-semibold text-sm">-</span>
                  <Input
                    id="end"
                    name="end"
                    type="date"
                    value={dates.end}
                    onChange={handleDateInputChange}
                    className="flex-1 min-w-0"
                  />
                </>
              )}
            </div>
            <div className="flex flex-col space-y-2 mt-4">
              <button
                type="button"
                onClick={() => {
                  setDates(fndDate => ({
                    ...fndDate,
                    showRange: false,
                    start: moment().format('YYYY-MM-DD'),
                    type: 'today',
                  }));
                }}
                className={`${
                  dates.type === 'today'
                    ? '!bg-[#8f7319] !text-pos-yellow font-medium'
                    : 'bg-pos-header-bg border'
                } text-sm text-left  rounded-md min-w-[150px] px-3 py-1 hover:opacity-90`}
              >
                Today
              </button>

              <button
                type="button"
                onClick={() => {
                  setDates(fndDate => ({
                    ...fndDate,
                    showRange: false,
                    start: moment().subtract(1, 'day').format('YYYY-MM-DD'),
                    type: 'yesterday',
                  }));
                }}
                className={`${
                  dates.type === 'yesterday'
                    ? '!bg-[#8f7319] !text-pos-yellow font-medium'
                    : 'bg-pos-header-bg border'
                } text-sm text-left  rounded-md min-w-[150px] px-3 py-1 hover:opacity-90`}
              >
                Yesterday
              </button>

              <button
                type="button"
                onClick={() => {
                  setDates(fndDate => ({
                    ...fndDate,
                    showRange: true,
                    start: moment().subtract(7, 'days').format('YYYY-MM-DD'),
                    end: moment().format('YYYY-MM-DD'),
                    type: 'last_7_days',
                  }));
                }}
                className={`${
                  dates.type === 'last_7_days'
                    ? '!bg-[#8f7319] !text-pos-yellow font-medium'
                    : 'bg-pos-header-bg border'
                } text-sm text-left  rounded-md min-w-[150px] px-3 py-1 hover:opacity-90`}
              >
                Last 7 days
              </button>

              <button
                type="button"
                onClick={() => {
                  setDates(fndDate => ({
                    ...fndDate,
                    showRange: true,
                    start: moment().subtract(30, 'days').format('YYYY-MM-DD'),
                    end: moment().format('YYYY-MM-DD'),
                    type: 'last_30_days',
                  }));
                }}
                className={`${
                  dates.type === 'last_30_days'
                    ? '!bg-[#8f7319] !text-pos-yellow font-medium'
                    : 'bg-pos-header-bg border'
                } text-sm text-left  rounded-md min-w-[150px] px-3 py-1 hover:opacity-90`}
              >
                Last 30 days
              </button>

              <button
                type="button"
                onClick={() => {
                  setDates(fndDate => ({
                    ...fndDate,
                    showRange: true,
                    start: moment().startOf('month').format('YYYY-MM-DD'),
                    end: moment().endOf('month').format('YYYY-MM-DD'),
                    type: 'this_month',
                  }));
                }}
                className={`${
                  dates.type === 'this_month'
                    ? '!bg-[#8f7319] !text-pos-yellow font-medium'
                    : 'bg-pos-header-bg border'
                } text-sm text-left  rounded-md min-w-[150px] px-3 py-1 hover:opacity-90`}
              >
                This Month
              </button>

              <button
                type="button"
                onClick={() => {
                  setDates(fndDate => ({
                    ...fndDate,
                    showRange: true,
                    start: moment()
                      .subtract(1, 'month')
                      .startOf('month')
                      .format('YYYY-MM-DD'),
                    end: moment()
                      .subtract(1, 'month')
                      .endOf('month')
                      .format('YYYY-MM-DD'),
                    type: 'last_month',
                  }));
                }}
                className={`${
                  dates.type === 'last_month'
                    ? '!bg-[#8f7319] !text-pos-yellow font-medium'
                    : 'bg-pos-header-bg border'
                } text-sm text-left  rounded-md min-w-[150px] px-3 py-1 hover:opacity-90`}
              >
                Last Month
              </button>

              <button
                type="button"
                onClick={() => {
                  setDates(fndDate => ({
                    ...fndDate,
                    showRange: true,
                    start: '',
                    end: '',
                    type: 'custom',
                  }));
                }}
                className={`${
                  dates.type === 'custom'
                    ? '!bg-[#8f7319] !text-pos-yellow font-medium'
                    : 'bg-pos-header-bg border'
                } text-sm text-left  rounded-md min-w-[150px] px-3 py-1 hover:opacity-90`}
              >
                Custom Range
              </button>
            </div>

            <div className="flex flex-row items-center mt-8 gap-3 w-full">
              <Button
                label="Apply"
                onClick={handleOnApplyDate}
                type="button"
                className="!bg-pos-green h-[40px] !text-white text-xbase border border-[rgba(27,31,36,0.15)]
              shadow-[0_1px_0_rgba(27,31,36,0.04),inset_0_1px_0_rgba(255,255,255,0.25)] font-medium flex-1 !min-w-0"
              />

              <Button
                label="Cancel"
                onClick={() => {
                  onApplyDate();
                  trigger();
                }}
                type="button"
                className="!bg-[#f6f8fa] h-[40px] !text-pos-black text-xbase border border-[rgba(27,31,36,0.15)]
              shadow-[0_1px_0_rgba(27,31,36,0.04),inset_0_1px_0_rgba(255,255,255,0.25)] font-medium flex-1 !min-w-0"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DateRangePicker;
